import React from "react";
import { Check } from "lucide-react";

/**
 * Catalogue filter rail — full sections on mobile + desktop; theme-aware.
 */
const CatalogDarkFilterSidebar = ({
  isDark = true,
  vehicleLine,
  hasVehicle,
  uniqueBrands = [],
  selectedBrands = [],
  onBrandToggle,
  brandsExpanded: _brandsExpanded,
  onBrandsExpandedChange: _onBrandsExpandedChange,
  priceMin,
  onPriceMinChange,
  priceMax,
  onPriceMaxChange,
  inStockOnly,
  onInStockOnlyChange,
  nextDayDelivery,
  onNextDayDeliveryChange,
  warranty6m,
  warranty1y,
  warranty2y,
  onWarranty6m,
  onWarranty1y,
  onWarranty2y,
  rating4Plus,
  onRating4PlusChange,
  rating3Plus = false,
  onRating3PlusChange = () => {},
  condNew = false,
  onCondNewChange = () => {},
  condUsedExcellent = false,
  onCondUsedExcellentChange = () => {},
  condUsedGood = false,
  onCondUsedGoodChange = () => {},
  condUsedFair = false,
  onCondUsedFairChange = () => {},
  counts = {},
  onReset,
}) => {
  const {
    inStock = 0,
    nextDay = 0,
    w6 = 0,
    w12 = 0,
    w24 = 0,
    rating4 = 0,
    rating3 = 0,
    condNew: countNew = 0,
    condUsedEx = 0,
    condUsedGood: countUsedGood = 0,
    condUsedFair: countUsedFair = 0,
  } = counts;

  const PRICE_MAX = 95000;
  const visibleBrands = uniqueBrands;
  const sliderValue = Math.min(Number(priceMax) || 0, PRICE_MAX);

  const Count = ({ n }) => (
    <span
      className={`text-[9px] sm:text-xs tabular-nums font-medium shrink-0 ${
        isDark ? "text-sky-400/90" : "text-slate-500"
      }`}
    >
      {n}
    </span>
  );

  const SectionTitle = ({ children }) => (
    <h3
      className={`mb-2 text-[9px] font-bold uppercase tracking-[0.12em] sm:mb-3 sm:text-[10px] ${
        isDark ? "text-white" : "text-slate-800"
      }`}
    >
      {children}
    </h3>
  );

  const Divider = () => (
    <div
      className={`my-3 sm:my-5 border-t ${
        isDark ? "border-slate-700/70" : "border-slate-200"
      }`}
    />
  );

  const CheckRow = ({ checked, onChange, label, count }) => (
    <label className="flex cursor-pointer items-center justify-between gap-1 py-0.5">
      <span className="flex min-w-0 items-center gap-1.5 sm:gap-2.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className={`h-3 w-3 shrink-0 rounded accent-amber-500 sm:h-4 sm:w-4 ${
            isDark ? "border-slate-500 bg-slate-900" : "border-slate-300 bg-white"
          }`}
        />
        <span
          className={`truncate text-[9px] leading-tight sm:text-sm ${
            isDark ? "text-slate-200" : "text-slate-700"
          }`}
        >
          {label}
        </span>
      </span>
      <Count n={count} />
    </label>
  );

  return (
    <aside
      className={`flex h-full max-h-[inherit] w-full flex-col rounded-lg p-2.5 sm:rounded-xl sm:p-4 ${
        isDark
          ? "border border-slate-700/90 bg-[#0c1018] shadow-lg shadow-black/30"
          : "border border-slate-200 bg-white shadow-sm"
      }`}
    >
      <div
        className={`mb-3 flex shrink-0 items-center justify-between pb-2 sm:mb-4 sm:pb-3 border-b ${
          isDark ? "border-slate-700/80" : "border-slate-200"
        }`}
      >
        <span
          className={`text-[10px] font-semibold tracking-wide sm:text-xs ${
            isDark ? "text-slate-300" : "text-slate-800"
          }`}
        >
          Filters
        </span>
        <button
          type="button"
          onClick={onReset}
          className={`text-[9px] font-bold uppercase tracking-wider sm:text-[10px] ${
            isDark ? "text-amber-400 hover:text-amber-300" : "text-amber-600 hover:text-amber-700"
          }`}
        >
          Reset
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 [-ms-overflow-style:auto] [scrollbar-width:thin]">
      {/* Vehicle */}
      <section>
        <SectionTitle>Vehicle</SectionTitle>
        {hasVehicle ? (
          <>
            <label className="group flex cursor-pointer items-center justify-between gap-1">
              <span className="flex min-w-0 items-center gap-1.5 sm:gap-2.5">
                <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-amber-500 bg-amber-500 sm:h-4 sm:w-4">
                  <Check className="h-2 w-2 text-white sm:h-2.5 sm:w-2.5" strokeWidth={3} />
                </span>
                <span
                  className={`truncate text-[9px] font-medium sm:text-sm ${
                    isDark ? "text-slate-100" : "text-slate-900"
                  }`}
                >
                  {vehicleLine}
                </span>
              </span>
            </label>
            <a
              href="/"
              onClick={() => {
                try { sessionStorage.setItem("jumpToVehicleSection", "1"); } catch {}
              }}
              className={`mt-1.5 inline-block text-[9px] font-semibold sm:mt-2 sm:text-xs ${
                isDark ? "text-amber-400 hover:text-amber-300" : "text-amber-600 hover:text-amber-700"
              }`}
            >
              Change vehicle
            </a>
          </>
        ) : (
          <p
            className={`text-[9px] leading-snug sm:text-xs ${
              isDark ? "text-slate-500" : "text-slate-500"
            }`}
          >
            Select a vehicle from the banner to filter by fitment.
          </p>
        )}
      </section>

      <Divider />

      {/* Brand */}
      <section>
        <SectionTitle>Brand</SectionTitle>
        <div className="space-y-1 sm:space-y-2">
          {visibleBrands.length === 0 ? (
            <p className={`text-[9px] sm:text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              No brands in results
            </p>
          ) : (
            visibleBrands.map((brand) => {
              const row = typeof brand === "string" ? { name: brand, n: 0 } : brand;
              const name = row.name ?? row;
              const n = row.n ?? 0;
              return (
                <CheckRow
                  key={name}
                  checked={selectedBrands.includes(name)}
                  onChange={() => onBrandToggle(name)}
                  label={name}
                  count={n}
                />
              );
            })
          )}
        </div>
      </section>

      <Divider />

      {/* Price range */}
      <section>
        <SectionTitle>Price range</SectionTitle>
        <input
          type="text"
          inputMode="numeric"
          value={priceMin}
          onChange={(e) => onPriceMinChange(e.target.value.replace(/\D/g, ""))}
          placeholder="₹ Min"
          className={`mb-2 w-full rounded-lg px-2 py-1.5 text-[10px] focus:outline-none focus:ring-2 focus:ring-amber-500/40 sm:mb-3 sm:px-3 sm:py-2 sm:text-sm ${
            isDark
              ? "border border-slate-600 bg-slate-950/80 text-sky-200/90 placeholder:text-slate-500"
              : "border border-slate-300 bg-slate-50 text-slate-800 placeholder:text-slate-400"
          }`}
        />
        <input
          type="range"
          min={0}
          max={PRICE_MAX}
          step={500}
          value={sliderValue}
          onChange={(e) => onPriceMaxChange(Number(e.target.value))}
          className={`h-1.5 w-full cursor-pointer appearance-none rounded-full accent-amber-500 sm:h-2 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500 sm:[&::-webkit-slider-thumb]:h-4 sm:[&::-webkit-slider-thumb]:w-4 ${
            isDark ? "bg-slate-800" : "bg-slate-200"
          }`}
        />
        <div
          className={`mt-1.5 flex justify-between text-[9px] tabular-nums sm:mt-2 sm:text-[11px] ${
            isDark ? "text-slate-500" : "text-slate-500"
          }`}
        >
          <span>₹0</span>
          <span>₹{PRICE_MAX.toLocaleString("en-IN")}</span>
        </div>
      </section>

      <Divider />

      {/* Availability */}
      <section>
        <SectionTitle>Availability</SectionTitle>
        <div className="space-y-1 sm:space-y-2">
          <CheckRow
            checked={inStockOnly}
            onChange={(e) => onInStockOnlyChange(e.target.checked)}
            label="In Stock"
            count={inStock}
          />
          <CheckRow
            checked={nextDayDelivery}
            onChange={(e) => onNextDayDeliveryChange(e.target.checked)}
            label="Next-Day Delivery"
            count={nextDay}
          />
        </div>
      </section>

      <Divider />

      {/* Warranty */}
      <section>
        <SectionTitle>Warranty</SectionTitle>
        <div className="space-y-1 sm:space-y-2">
          <CheckRow checked={warranty6m} onChange={(e) => onWarranty6m(e.target.checked)} label="6 months+" count={w6} />
          <CheckRow checked={warranty1y} onChange={(e) => onWarranty1y(e.target.checked)} label="1 year+" count={w12} />
          <CheckRow checked={warranty2y} onChange={(e) => onWarranty2y(e.target.checked)} label="2 years+" count={w24} />
        </div>
      </section>

      <Divider />

      {/* Rating */}
      <section>
        <SectionTitle>Rating</SectionTitle>
        <div className="space-y-1 sm:space-y-2">
          <CheckRow
            checked={rating4Plus}
            onChange={(e) => onRating4PlusChange(e.target.checked)}
            label={
              <>
                4<span className={isDark ? "text-white" : "text-amber-500"}>★</span> and above
              </>
            }
            count={rating4}
          />
          <CheckRow
            checked={rating3Plus}
            onChange={(e) => onRating3PlusChange(e.target.checked)}
            label={
              <>
                3<span className={isDark ? "text-white" : "text-amber-500"}>★</span> and above
              </>
            }
            count={rating3}
          />
        </div>
      </section>

      <Divider />

      {/* Condition */}
      <section>
        <SectionTitle>Condition</SectionTitle>
        <div className="space-y-1 sm:space-y-2">
          <CheckRow checked={condNew} onChange={(e) => onCondNewChange(e.target.checked)} label="New" count={countNew} />
          <CheckRow
            checked={condUsedExcellent}
            onChange={(e) => onCondUsedExcellentChange(e.target.checked)}
            label="Used — Excellent"
            count={condUsedEx}
          />
          <CheckRow
            checked={condUsedGood}
            onChange={(e) => onCondUsedGoodChange(e.target.checked)}
            label="Used — Good Condition"
            count={countUsedGood}
          />
          <CheckRow
            checked={condUsedFair}
            onChange={(e) => onCondUsedFairChange(e.target.checked)}
            label="Used — Fair"
            count={countUsedFair}
          />
        </div>
      </section>
      </div>
    </aside>
  );
};

export default CatalogDarkFilterSidebar;
